/**
 * SunoReplaceJobsContext — фоновые задачи «Заменить фрагмент трека».
 *
 * По структуре аналогичен `SunoCoverJobsContext`, но проще: результата-выбора
 * нет — по завершении Suno worker сохраняет НОВЫЙ трек (operation =
 * 'replace_section') в suno_tracks, и библиотека подхватывает его по событию
 * `suno-track-generated`.
 *
 * Зачем глобальный контекст (а не polling внутри шита): юзер запускает замену
 * и закрывает шит — генерация идёт 1-3 минуты. Polling переживает закрытие
 * шита и reload страницы (localStorage), на строке исходного трека крутится
 * статус-пилюля «Фрагмент» (SunoTrackList.activeOps), по готовности — тост.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { replaceSection, type SunoTrack } from '../api/music'
import { waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'
import { translateSunoError } from '../pages/generate/utils/sunoErrors'

const LS_PENDING_KEY = 'suno_replace_pending_v1'
const POLL_INTERVAL_MS = 10_000

interface PendingReplaceJob {
	trackId: string
	requestId: string
	title: string | null
	startedAt: number
}

export interface ReplaceSectionParams {
	prompt?: string
	style?: string
	/** Название нового трека-результата; пусто — бэк наследует от исходника. */
	title?: string
	replace_start: number
	replace_end: number
	/** UUID персоны — для Mureka голос заменённого фрагмента (Suno игнорирует). */
	persona_id?: string | null
}

interface SunoReplaceJobsValue {
	/** Запуск замены. true — job принят (шит можно закрывать). */
	start: (track: SunoTrack, params: ReplaceSectionParams) => Promise<boolean>
	isRunning: (trackId: string) => boolean
}

const SunoReplaceJobsContext = createContext<SunoReplaceJobsValue | null>(null)

// ─── localStorage helpers (паттерн SunoCoverJobsContext) ───────────
function loadPending(): Record<string, PendingReplaceJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingReplaceJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingReplaceJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3): прежние
				// 30 минут бросали следить за долгой задачей после перезагрузки.
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingReplaceJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch {
		/* приватный режим — job просто не переживёт reload */
	}
}

export function SunoReplaceJobsProvider({ children }: { children: ReactNode }) {
	const [runningIds, setRunningIds] = useState<Set<string>>(() => new Set(Object.keys(loadPending())))
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingReplaceJob>>(loadPending())

	const markRunning = useCallback((trackId: string, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingReplaceJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	// ─── Core polling loop ────────────────────────────────────────
	const runPolling = useCallback(
		async (trackId: string, requestId: string, trackTitle: string | null) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title: trackTitle,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, {
					minDelayMs: POLL_INTERVAL_MS,
					startedAt: pendingRef.current[trackId]?.startedAt,
				})
				if (s?.status === 'completed') {
					showToast(
						`Фрагмент заменён${trackTitle ? ` в «${trackTitle}»` : ''} — новый трек в «Доработках»`,
						'success',
					)
					// Библиотека слушает это событие и перезагружает список —
					// новый трек (operation='replace_section') появится сверху.
					try {
						window.dispatchEvent(new CustomEvent('suno-track-generated', { detail: { sourceTrackId: trackId } }))
					} catch { /* event optional */ }
				} else if (s) {
					showToast(
						translateSunoError(s.error_message_ru || s.error_message || 'Замена фрагмента не удалась'),
						'error',
					)
				}
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, setPendingJob],
	)

	// ─── start(): внешний API ─────────────────────────────────────
	const start = useCallback(
		async (track: SunoTrack, params: ReplaceSectionParams): Promise<boolean> => {
			const trackId = track.id
			if (activeRef.current.has(trackId)) {
				showToast('Замена фрагмента уже идёт для этого трека...', 'info')
				return false
			}

			const res = await replaceSection({ track_id: trackId, ...params })
			if (!res.ok || !res.request_id) {
				showToast(translateSunoError(res.error || 'Ошибка запуска замены фрагмента'), 'error')
				return false
			}

			showToast('Замена фрагмента запущена — следи за статусом на треке', 'info')
			void runPolling(trackId, res.request_id, track.title)
			return true
		},
		[runPolling],
	)

	// ─── Restore pending jobs после reload ────────────────────────
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				void runPolling(trackId, job.requestId, job.title)
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const isRunning = useCallback((trackId: string) => runningIds.has(trackId), [runningIds])

	const value = useMemo<SunoReplaceJobsValue>(() => ({ start, isRunning }), [start, isRunning])

	return <SunoReplaceJobsContext.Provider value={value}>{children}</SunoReplaceJobsContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSunoReplaceJobs(): SunoReplaceJobsValue {
	const ctx = useContext(SunoReplaceJobsContext)
	if (!ctx) {
		return {
			start: async () => false,
			isRunning: () => false,
		}
	}
	return ctx
}
